import MarketplaceService from "../../../../service/Makertplace";
import ProductService from "../../../../service/ProductService";
import { AdminApiError } from "../../hooks/useAdminList";
import { getToken } from "../../utils/adminUtils";

export const LANGS = [
  { code: "pt", suffix: "Portuguese" },
  { code: "en", suffix: "English" },
  { code: "it", suffix: "Italian" },
  { code: "es", suffix: "Spanish" },
];

export const DEVICES = [
  { key: "desktop", prefix: "imageDesktop" },
  { key: "mobile", prefix: "imageMobile" },
];

export const slotField = (device, lang) =>
  `${DEVICES.find((d) => d.key === device).prefix}${LANGS.find((l) => l.code === lang).suffix}`;

// As 8 imagens de um banner (desktop/mobile x 4 idiomas)
export const SLOT_FIELDS = DEVICES.flatMap((device) =>
  LANGS.map((lang) => ({
    field: slotField(device.key, lang.code),
    device: device.key,
    lang: lang.code,
  })),
);

// Configuração de cada tipo de banner: onde fica na resposta da API e quais endpoints usa
export const BANNER_TYPES = {
  main: {
    ratio: 3 / 1,
    ratioLabel: "3:1",
    primaryKey: "mainBanner",
    extrasKey: "mainBanners",
    delayField: "delayMainBanner",
    saveExtra: (service, data) => service.postUploadNewMainBanner(data),
    deleteExtra: (service, id) => service.deleteMainBanner(id),
  },
  sub1: {
    ratio: 7 / 4,
    ratioLabel: "7:4",
    primaryKey: "subBanner1",
    extrasKey: "subBanners1",
    delayField: "delaySubBanners",
    saveExtra: (service, data) => service.postUploadNewSubBanner1(data),
    deleteExtra: (service, id) => service.deleteSubBanner1(id),
  },
  sub2: {
    ratio: 7 / 4,
    ratioLabel: "7:4",
    primaryKey: "subBanner2",
    extrasKey: "subBanners2",
    delayField: "delaySubBanners",
    saveExtra: (service, data) => service.postUploadNewSubBanner2(data),
    deleteExtra: (service, id) => service.deleteSubBanner2(id),
  },
  sub3: {
    ratio: 7 / 4,
    ratioLabel: "7:4",
    primaryKey: "subBanner3",
    extrasKey: "subBanners3",
    delayField: "delaySubBanners",
    saveExtra: (service, data) => service.postUploadNewSubBanner3(data),
    deleteExtra: (service, id) => service.deleteSubBanner3(id),
  },
};

let keySeq = 0;
const nextKey = () => `banner-${++keySeq}`;

// Valor de imagem: string (id no Cloudinary, já salvo), File (escolhido agora) ou null
const toBanner = (source, { primary }) => ({
  key: nextKey(),
  primary,
  original: source || {},
  showImage: primary ? !!source?.showImage : source?.showImage !== false,
  urlDestination: source?.urlDestination || "",
  images: Object.fromEntries(
    SLOT_FIELDS.map(({ field }) => [field, typeof source?.[field] === "string" ? source[field] : null]),
  ),
});

export const emptyBanner = () => toBanner({ showImage: true }, { primary: false });

export const loadBannerConfig = async (type) => {
  const config = BANNER_TYPES[type];
  const res = await new MarketplaceService().getConfiguration();
  const result = res?.result || {};
  return {
    delays: {
      delayMainBanner: result.delayMainBanner,
      delaySubBanners: result.delaySubBanners,
    },
    banners: [
      toBanner(result[config.primaryKey], { primary: true }),
      ...(result[config.extrasKey] || []).map((extra) => toBanner(extra, { primary: false })),
    ],
  };
};

const uploadImage = async (file) => {
  const res = await new ProductService().postUploadImage(getToken(), file);
  if (res?.messageCode !== 200000 || !res?.result) {
    throw new AdminApiError(res?.messageCode ?? 500000);
  }
  return res.result;
};

// Envia as imagens novas antes de salvar. Se alguma falhar, nada é salvo
// (o clássico seguia em frente e gravava o banner sem a imagem).
const uploadBannerImages = async (banner) => {
  const images = {};
  for (const { field } of SLOT_FIELDS) {
    const value = banner.images[field];
    images[field] = value instanceof File ? await uploadImage(value) : value;
  }
  return images;
};

export const saveBannerConfig = async ({ type, banners, removedIds, delays }) => {
  const config = BANNER_TYPES[type];
  const service = new MarketplaceService();

  const uploaded = [];
  for (const banner of banners) {
    uploaded.push({ banner, images: await uploadBannerImages(banner) });
  }

  // Como no clássico, falha ao salvar o tempo de rotação não impede o resto
  try {
    await service.postDelayBanners(delays);
  } catch (e) {
    console.log(e);
  }

  for (const id of removedIds) {
    await config.deleteExtra(service, id);
  }

  const [primary, ...extras] = uploaded;
  await service.postUploadBannner(
    { ...primary.images, showImage: primary.banner.showImage, urlDestination: primary.banner.urlDestination },
    type,
  );

  for (const { banner, images } of extras) {
    await config.saveExtra(service, {
      ...banner.original,
      ...images,
      showImage: banner.showImage,
      urlDestination: banner.urlDestination,
    });
  }
};

export const hasAnyImage = (banner) => SLOT_FIELDS.some(({ field }) => !!banner.images[field]);

export const countImages = (banner) =>
  SLOT_FIELDS.filter(({ field }) => !!banner.images[field]).length;
